/**
 * acquisitionTouchReporter.ts
 *
 * WHY THIS EXISTS (Rob, 2026-10-21): every BotSpot purchase must trace back to
 * the YouTube video (or other content) that brought the buyer. Media Studio
 * links carry utm_content=<YouTube video id>. A signed-in person who clicked a
 * new link was never recorded, so Node could not tie their later payment to
 * the video. This sends the browser's stored campaign link (with its first
 * touch) to Node's POST /users/acquisition-touch, once per touch per account.
 *
 * Node keeps the first touch forever and records this one as the latest.
 */
import { getStoredUtmParams, getUtmParamsForApi } from './utmTracker';

export const REPORTED_TOUCH_KEY_PREFIX = 'botspot_reported_acquisition_touch:';

const SIGNATURE_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_placement',
  'first_touch_utm_source',
  'first_touch_utm_content',
  'timestamp',
] as const;

type SignatureKey = (typeof SIGNATURE_KEYS)[number];
// The fields utmTracker.js stores for one campaign link (strings, plus a
// numeric `timestamp` set when the link was first seen).
type StoredCampaignLink = Partial<Record<SignatureKey, string | number>>;

export type AcquisitionTouchReportResult =
  | 'reported'
  | 'already_reported'
  | 'no_touch'
  | 'no_token'
  | 'failed';

type FetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body: string },
) => Promise<{ ok: boolean; status: number }>;

export type ReportAcquisitionTouchOptions = {
  userKey: string;
  apiUrl: string | undefined;
  getToken: () => Promise<string | null | undefined>;
  fetchImpl?: FetchLike;
};

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode: the touch may be reported again later, which Node ignores safely.
  }
}

export async function reportAcquisitionTouch({
  userKey,
  apiUrl,
  getToken,
  fetchImpl = (url, init) => fetch(url, init),
}: ReportAcquisitionTouchOptions): Promise<AcquisitionTouchReportResult> {
  const stored: StoredCampaignLink | null = getStoredUtmParams();
  const hasCampaign =
    stored && SIGNATURE_KEYS.some((key) => key !== 'timestamp' && Boolean(stored[key]));
  if (!stored || !hasCampaign || !userKey || !apiUrl) return 'no_touch';

  const signature = JSON.stringify(SIGNATURE_KEYS.map((key) => stored[key] ?? null));
  const storageKey = `${REPORTED_TOUCH_KEY_PREFIX}${userKey}`;
  if (readStorage(storageKey) === signature) return 'already_reported';

  const token = await getToken().catch(() => null);
  if (!token) return 'no_token';

  try {
    const response = await fetchImpl(`${apiUrl}/users/acquisition-touch`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(getUtmParamsForApi() || {}),
    });
    if (!response.ok) return 'failed';
  } catch {
    return 'failed';
  }
  writeStorage(storageKey, signature);
  return 'reported';
}
